import React, { useState, useEffect } from 'react';
import { VoiceNote } from 'lib/redux/linkedin/voice-memos/slice';
import { LinkedInVoiceMemoMetadata } from 'interfaces/db';
import { Typography } from 'interfaces/typography';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Public as PublicIcon,
  AccessTime as AccessTimeIcon,
  CloudDownload as CloudDownloadIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface VoiceNoteTileProps {
  voiceNote: VoiceNote;
  userId: string | undefined;
  currentlyPlaying: string | null;
  loadingStates: Record<string, boolean>;
  onPlay: (id: string) => void;
  onEdit: (voiceNote: LinkedInVoiceMemoMetadata) => void;
  onOpenActionMenu: (event: React.MouseEvent<HTMLButtonElement>, voiceNoteId: string) => void;
  onDelete: (voiceNoteId: string, skipConfirmation?: boolean) => void;
}

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const getAudioDuration = async (blob: Blob): Promise<number> => {
  return new Promise((resolve) => {
    const audio = new Audio(URL.createObjectURL(blob));
    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(audio.src);
      resolve(audio.duration);
    });
  });
};

export const VoiceNoteTile: React.FC<VoiceNoteTileProps> = ({
  voiceNote,
  userId,
  currentlyPlaying,
  loadingStates,
  onPlay,
  onEdit,
  onOpenActionMenu,
  onDelete,
}) => {
  const theme = useTheme();
  const [duration, setDuration] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(voiceNote.name);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const isCurrentlyPlaying = currentlyPlaying === voiceNote.linkedin_voice_memo_id;
  const isLoading = loadingStates[voiceNote.linkedin_voice_memo_id];
  const isOwner = voiceNote.user_id === userId;
  const hasBlob = Boolean(voiceNote.blob);

  useEffect(() => {
    const loadDuration = async () => {
      if (voiceNote.blob) {
        const audioDuration = await getAudioDuration(voiceNote.blob);
        setDuration(audioDuration);
      }
    };

    if (hasBlob && !duration) {
      loadDuration();
    }
  }, [voiceNote.blob, hasBlob, duration]);

  const handleSaveEdit = () => {
    if (editedName.trim() && editedName !== voiceNote.name) {
      onEdit({
        linkedin_voice_memo_id: voiceNote.linkedin_voice_memo_id,
        name: editedName,
        user_id: voiceNote.user_id || '',
        team_id: voiceNote.team_id || '',
        file_size: voiceNote.file_size
      });
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setEditedName(voiceNote.name);
      setIsEditing(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgb(229, 231, 235)',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 16px -8px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <CardContent sx={{ 
        p: 1.5,
        pb: '12px',
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        gap: 1
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          gap: 1,
          minHeight: 24
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyPress}
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            ) : (
              <Typography
                variant="h4"
                color="rgb(17, 24, 39)"
                fontWeight={500}
                fontSize="0.875rem"
                style={{
                  lineHeight: 1.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {voiceNote.name}
              </Typography>
            )}
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexShrink: 0 
          }}>
            {hasBlob && duration !== null && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                color: 'rgb(107, 114, 128)',
                fontSize: '0.75rem'
              }}>
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                <Typography 
                  variant="smallParagraph" 
                  fontSize="0.75rem" 
                  color="rgb(107, 114, 128)"
                  style={{ lineHeight: 1, display: 'inline' }}
                >
                  {formatDuration(duration)}
                </Typography>
              </Box>
            )}
            {!isOwner && (
              <Tooltip title="Shared voice note">
                <Chip
                  icon={<PublicIcon sx={{ fontSize: 14 }} />}
                  label="Shared"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    border: 'none',
                    fontWeight: 500
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ flex: '1 1 0', minHeight: 4 }} />

        <Button
          fullWidth
          variant="outlined"
          onClick={() => onPlay(voiceNote.linkedin_voice_memo_id)}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={14} />
            ) : !hasBlob ? (
              <CloudDownloadIcon sx={{ fontSize: 16 }} />
            ) : isCurrentlyPlaying ? (
              <PauseIcon sx={{ fontSize: 16 }} />
            ) : (
              <PlayIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            height: 28,
            minHeight: 28,
            borderColor: 'rgb(229, 231, 235)',
            color: isCurrentlyPlaying ? theme.palette.primary.main : 'rgb(75, 85, 99)',
            backgroundColor: isCurrentlyPlaying ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.04)
            },
            fontSize: '0.75rem',
            textTransform: 'none',
            padding: '4px 8px'
          }}
        >
          {isLoading ? 'Loading...' : !hasBlob ? 'Load' : isCurrentlyPlaying ? 'Pause' : 'Play'}
        </Button>
      </CardContent>

      {isOwner && (
        <CardActions sx={{ 
          px: 1.5,
          py: 0.5,
          borderTop: '1px solid rgb(243, 244, 246)',
          display: 'flex',
          justifyContent: 'center',
          gap: 0.5,
          minHeight: 32
        }}>
          {isEditing ? (
            <>
              <IconButton
                size="small"
                onClick={handleSaveEdit}
                sx={{
                  padding: '2px',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                <SaveIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setEditedName(voiceNote.name);
                  setIsEditing(false);
                }}
                sx={{
                  padding: '2px',
                  color: 'rgb(107, 114, 128)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={() => setIsEditing(true)}
                sx={{
                  padding: '2px',
                  color: 'rgb(107, 114, 128)',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    color: theme.palette.primary.main
                  }
                }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  padding: '2px',
                  color: 'rgb(107, 114, 128)',
                  '&:hover': {
                    backgroundColor: alpha('rgb(239, 68, 68)', 0.04),
                    color: 'rgb(239, 68, 68)'
                  }
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}
        </CardActions>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 1,
            border: '1px solid rgb(229, 231, 235)',
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2,
          pb: 1,
          color: 'rgb(239, 68, 68)'
        }}>
          Delete Voice Note
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1 }}>
          <Typography variant="mediumParagraph" color="rgb(75, 85, 99)">
            Are you sure you want to delete "{voiceNote.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2,
          pt: 1,
          gap: 1
        }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ 
              color: 'rgb(75, 85, 99)',
              textTransform: 'none',
              borderColor: 'rgb(229, 231, 235)',
              '&:hover': {
                borderColor: 'rgb(107, 114, 128)',
                backgroundColor: 'transparent'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDelete(voiceNote.linkedin_voice_memo_id, true);
              setDeleteDialogOpen(false);
            }}
            variant="contained"
            sx={{
              bgcolor: 'rgb(239, 68, 68)',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgb(220, 38, 38)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}; 